'use client';

import { useEffect, useMemo, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  AdobePageData,
  buildAdobePageData,
  isAdobeAnalyticsEnabled,
  pushAdobeNewPage,
} from '../../../lib/util/adobeAnalytics';

const buildRoutePageData = (
  asPath: string,
  pathname: string | null,
): AdobePageData => {
  const pageData = buildAdobePageData({ asPath, pathname });
  return {
    ...pageData,
    visitor: window.pageData?.visitor || pageData.visitor,
  };
};

const isLastAdobeEventForPage = (pageName: string): boolean => {
  const lastEvent = window.appData?.[window.appData.length - 1];
  return lastEvent?.page?.name === pageName;
};

/**
 * Pushes Adobe newPage events for client-side route changes.
 */
const AdobeAnalyticsRouteTracker = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedAsPath = useRef<string | null>(null);

  const asPath = useMemo(() => {
    const search = searchParams?.toString();
    const currentPath = pathname;

    if (!currentPath) return null;

    return search ? `${currentPath}?${search}` : currentPath;
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!isAdobeAnalyticsEnabled()) return;
    if (!asPath) return;

    if (!lastTrackedAsPath.current) {
      lastTrackedAsPath.current = asPath;
      return;
    }

    if (lastTrackedAsPath.current === asPath) return;

    lastTrackedAsPath.current = asPath;

    const pageData = buildRoutePageData(asPath, pathname);
    if (isLastAdobeEventForPage(pageData.page.name)) return;

    pushAdobeNewPage(pageData);
  }, [asPath, pathname]);

  return null;
};

export default AdobeAnalyticsRouteTracker;
